import {useState} from "react";
import baseUrl from "../../../baseUrl";
import {requester} from "../../../services/requester";
import {useAppContext} from "../../../contexts/AppContext";
import {UserSettlementsPicker} from "./UserSettlementsPicker";

// Създаване (POST /register) или редакция на потребител. При редакция:
// role/active/can_reports → PUT /admin/users/<id>; парола (по избор) → отделен
// ендпойнт; права по землища (само за роля 'user') → отделен ендпойнт.
// Собственият запис: role/active не се пращат и са заключени в UI (сървърът
// отхвърля промяната им — „заключване навън").
export const AdminUserForm = ({user, onSaved, onCancel}) => {
    const isNew = !user.id;
    const {user: me} = useAppContext();
    const isSelf = !isNew && user.id === me?.id;
    const request = requester();

    const [username, setUsername] = useState(user.username || '');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState(user.role || 'user');
    const [active, setActive] = useState(user.active !== false);
    const [canReports, setCanReports] = useState(user.can_reports !== false);
    const [ekattes, setEkattes] = useState(user.settlements || []);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    // И2: контакти — за да може админът да отговори на въпрос по телефон или
    // имейл, когато писането в системата не е достатъчно.
    const [fullName, setFullName] = useState(user.full_name || '');
    const [email, setEmail] = useState(user.email || '');
    const [phone, setPhone] = useState(user.phone || '');
    const [note, setNote] = useState(user.note || '');

    const contacts = {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        note: note.trim(),
    };

    const save = async (e) => {
        e.preventDefault();
        setError('');

        if (isNew && (!username.trim() || password.length < 6)) {
            setError('Потребителско име е задължително, паролата — поне 6 знака.');
            return;
        }

        setSaving(true);
        try {
            if (isNew) {
                const created = await request.post(`${baseUrl()}/register`, {
                    username: username.trim(), password, role, can_reports: canReports,
                    ...contacts,
                });
                if (role !== 'admin' && ekattes.length) {
                    await request.put(`${baseUrl()}/admin/users/${created.id}/settlements`, {ekattes});
                }
            } else {
                await request.put(`${baseUrl()}/admin/users/${user.id}`,
                    isSelf
                        ? {can_reports: canReports, ...contacts}
                        : {role, active, can_reports: canReports, ...contacts});
                if (password) {
                    if (password.length < 6) {
                        setError('Паролата трябва да е поне 6 знака.');
                        setSaving(false);
                        return;
                    }
                    await request.put(`${baseUrl()}/admin/users/${user.id}/password`, {password});
                }
                if (role !== 'admin') {
                    await request.put(`${baseUrl()}/admin/users/${user.id}/settlements`, {ekattes});
                }
            }
            onSaved();
        } catch (err) {
            setError(err.message || 'Грешка при запис.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className="border rounded p-3 mb-4 bg-body-tertiary" onSubmit={save}>
            <h5 className="mb-3">{isNew ? 'Нов потребител' : `Редакция: ${user.username}`}</h5>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="row g-3">
                <div className="col-md-4">
                    <label className="form-label">Потребителско име</label>
                    <input className="form-control" value={username} disabled={!isNew}
                           onChange={e => setUsername(e.target.value)}/>
                </div>
                <div className="col-md-4">
                    <label className="form-label">
                        Парола {isNew ? '' : '(остави празно за без промяна)'}
                    </label>
                    <input type="password" className="form-control" value={password}
                           autoComplete="new-password"
                           onChange={e => setPassword(e.target.value)}/>
                </div>
                <div className="col-md-4">
                    <label className="form-label">Роля</label>
                    <select className="form-select" value={role} disabled={isSelf}
                            title={isSelf ? 'Собствената роля не може да се променя.' : undefined}
                            onChange={e => setRole(e.target.value)}>
                        <option value="user">потребител</option>
                        <option value="admin">администратор</option>
                    </select>
                </div>
            </div>

            <div className="d-flex gap-4 mt-3">
                <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="canReports"
                           checked={canReports} onChange={e => setCanReports(e.target.checked)}/>
                    <label className="form-check-label" htmlFor="canReports">Достъп до „Справки"</label>
                </div>
                {!isNew && (
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="active"
                               checked={active} disabled={isSelf}
                               title={isSelf ? 'Собственият достъп не може да се спира.' : undefined}
                               onChange={e => setActive(e.target.checked)}/>
                        <label className="form-check-label" htmlFor="active">Активен достъп</label>
                    </div>
                )}
            </div>

            {/* И2: контакти — за отговор по въпрос извън системата. Всички са по избор. */}
            <fieldset className="mt-3">
                <legend className="form-label mb-2">Контакти (по избор)</legend>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label className="form-label">Име и фамилия</label>
                        <input className="form-control" value={fullName} maxLength={120}
                               onChange={e => setFullName(e.target.value)}/>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Имейл</label>
                        <input type="email" className="form-control" value={email} maxLength={120}
                               onChange={e => setEmail(e.target.value)}/>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Телефон</label>
                        <input className="form-control" value={phone} maxLength={40}
                               onChange={e => setPhone(e.target.value)}/>
                    </div>
                    <div className="col-12">
                        <label className="form-label">Бележка</label>
                        <textarea className="form-control" rows={2} value={note}
                                  onChange={e => setNote(e.target.value)}/>
                    </div>
                </div>
            </fieldset>

            {role !== 'admin' && (
                <div className="mt-3">
                    <label className="form-label">Права по землища</label>
                    <UserSettlementsPicker value={ekattes} onChange={setEkattes}/>
                </div>
            )}

            <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Запис…' : 'Запис'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
                    Отказ
                </button>
            </div>
        </form>
    );
};
